import React, { useEffect, useRef, useState } from 'react';

import { useMediaQuery } from '@mui/material';
import { Theme, useTheme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';

import { BackLink, Button, Snackbar } from '../../components/atoms';
import { BottomBar, LoadingSpinner, Page } from '../../components/layouts';
import { OrderSummaryLine } from '../../components/molecules';
import { ReceiptButton } from '../../components/molecules/ReceiptButton';
import { OrderStatus } from '../../components/partials';
import { ContactRestaurantInfoBox } from '../../components/partials/ContactRestaurantInfoBox';
import { OrderDescriptionList } from '../../components/partials/OrderDescriptionList';
import { PageHeader } from '../../components/partials/PageHeader';
import { analyticsService, flagService } from '../../core/services';
import { FulfilmentStatus } from '../../core/state/order/order.types';
import { getFulfilmentStatus } from '../../core/state/order/orderMiddleware';
import { storeListUtils } from '../../utils';
import { useInterval } from '../../utils/hooks';
import { deliveryKey } from '../../utils/makeUtils';
import {
  getOrderCollectionOrDeliveryTime,
  getOrderStatusPollDelay,
  getOrderStatusViewedEventData,
  getTaxLabel,
  getUnvailableOrderItemIds,
  reorder,
} from '../../utils/orderUtils';
import * as Api from '../../weborder/Api';
import { orderHistoryUtils } from '../OrderHistory/utils';
import { DispatchProps, StateProps } from './index';
import { HistoryItemsList } from './partials';

type Props = StateProps & DispatchProps;

const useStyles = makeStyles((theme: Theme) => ({
  header: {
    marginBottom: 0,
  },
  orderDetailList: {
    padding: theme.spacing(0, 2),
    '& li': {
      borderBottom: `1px solid ${theme.palette.grey[400]}`,
      '&:last-of-type': {
        border: 'unset',
      },
    },

    '&.orderedConcessionItemsSummary': {
      '& li:first-child': {
        border: 'unset',
      },
      '& ul': {
        '& li:last-of-type': {
          border: 'unset',
        },
      },
    },
  },

  receiptButtonContainer: {
    display: 'flex',
    flexFlow: 'column nowrap',
    [theme.breakpoints.up('md')]: {
      flexFlow: 'row nowrap',
      marginTop: theme.spacing(2),
    },
  },

  reorderButton: {
    marginRight: 0,
    marginTop: theme.spacing(1.25),
  },

  snackbar: {
    margin: theme.spacing(1, 2),
  },

  [theme.breakpoints.up('sm')]: {
    orderDetailList: {
      padding: theme.spacing(0, 3),
    },
    snackbar: {
      margin: theme.spacing(1, 3),
    },
  },

  [theme.breakpoints.up('md')]: {
    orderDetailList: {
      padding: 0,
    },
    reorderButton: {
      marginRight: theme.spacing(1.25),
      marginTop: 0,
    },
    snackbar: {
      margin: theme.spacing(1, 0),
    },
  },
}));

export function OrderHistoryDetail(props: Props): JSX.Element {
  const {
    appConfig,
    actions,
    collectionRestaurants,
    currentLang,
    deliveryLocations,
    deliveryRestaurants,
    menu,
    menuUrl,
    orderDetails,
    ordersHistoryStatus,
    restaurantDetail,
    translate,
  } = props;

  const isReorderEnabled = flagService.isFlagOn('reorderOrderHistoryDetails');
  const isOrderStatusEnabled = flagService.isFlagOn('orderStatus');

  const statusTimer = useRef<NodeJS.Timeout>();
  const classes = useStyles();
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme?.breakpoints.down('md'));
  const wasHistoryRequestedFlag = useRef(false);
  const [orderStatus, setOrderStatus] = useState<FulfilmentStatus>();
  const [orderTime, setOrderTime] = useState<string>(undefined);
  const [unavailableOrderItemIds, setUnavailableOrderItemIds] = useState<
    {
      orderItemId: number;
      orderItemName: string;
    }[]
  >([]);
  const [isReorderAvailable, setIsReorderAvailable] = useState<boolean>(false);

  const hasReceiptCode = orderDetails?.ReceiptCode !== null;
  const areAllOrderItemsUnavailable =
    unavailableOrderItemIds.length === orderDetails?.OrderHistoryItems?.length;

  const isOrderDeliveryAndDeliveryLocationDoesNotExist =
    orderDetails?.DeliveryType === Api.DeliveryType.Delivery
      ? !deliveryLocations ||
        deliveryLocations[orderDetails?.DeliveryLocationId]?.status !==
          'success'
      : false;

  const showReorderButton =
    isReorderEnabled &&
    menu &&
    isReorderAvailable &&
    !areAllOrderItemsUnavailable &&
    !isOrderDeliveryAndDeliveryLocationDoesNotExist;

  const loadOrderStatus = (): Promise<void> =>
    getFulfilmentStatus(Number(orderDetails.OrderId))
      .then((fulfilmentStatus) => {
        setOrderStatus(fulfilmentStatus);
        trackOrderStatus(fulfilmentStatus);
      })
      .catch(() => {
        setOrderStatus(null);
      });

  const restaurantPhoneNumber = storeListUtils.getRestaurantPhoneNumber(
    orderDetails?.PhysicalRestaurantId,
    collectionRestaurants,
  );

  const refetchOrderStatusDelay = isOrderStatusEnabled
    ? getOrderStatusPollDelay(orderStatus)
    : null;

  useInterval(loadOrderStatus, refetchOrderStatusDelay);

  const handleVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') {
      statusTimer.current = setTimeout(() => {
        loadOrderStatus();
      }, 500);
    }
  };

  useEffect(() => {
    if (isReorderEnabled && isReorderAvailable) {
      if (menu) {
        const unavailableItems = getUnvailableOrderItemIds(
          menu.MenuSections,
          orderDetails.OrderHistoryItems,
        );
        setUnavailableOrderItemIds(unavailableItems);
      } else if (menuUrl) {
        actions.getMenuByUrl(menuUrl);
      }
    }
  }, [menu, menuUrl, isReorderAvailable]);

  useEffect(() => {
    const isRestaurantClosedOrUndefined =
      !restaurantDetail ||
      (!restaurantDetail.IsOpen && !restaurantDetail.AcceptPreOrders);

    if (isReorderEnabled && !isRestaurantClosedOrUndefined) {
      setIsReorderAvailable(true);
    }
  }, [restaurantDetail]);

  useEffect(() => {
    const time = getOrderCollectionOrDeliveryTime(
      orderDetails,
      orderStatus,
      collectionRestaurants,
      deliveryRestaurants,
      translate,
    );
    setOrderTime(time);
  }, [collectionRestaurants, deliveryRestaurants, orderDetails, orderStatus]);

  useEffect(() => {
    if (
      ordersHistoryStatus === 'pending' &&
      wasHistoryRequestedFlag.current === false
    ) {
      actions.getOrderHistory();
      wasHistoryRequestedFlag.current = true;
    }
  }, [ordersHistoryStatus]);

  useEffect(() => {
    if (isOrderStatusEnabled && orderDetails) {
      setOrderStatus(orderDetails.FulfillmentStatus);
      document.addEventListener('visibilitychange', handleVisibilityChange);
      if (wasHistoryRequestedFlag.current === false) {
        loadOrderStatus();
      } else {
        // track OrderStatus from ordersHistory endpoint
        trackOrderStatus(orderDetails.FulfillmentStatus);
      }
    }
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (statusTimer.current) {
        clearTimeout(statusTimer.current);
      }
    };
  }, [orderDetails]);

  useEffect(() => {
    if (
      isOrderStatusEnabled &&
      orderDetails?.DeliveryType === Api.DeliveryType.Collection &&
      collectionRestaurants &&
      !Object.keys(collectionRestaurants).includes(
        orderDetails?.PhysicalRestaurantId.toString(),
      )
    ) {
      actions.getRestaurantData(
        orderDetails.VirtualRestaurantId,
        'collection',
        orderDetails.PhysicalRestaurantId,
      );
    }
  }, [orderDetails, collectionRestaurants]);

  useEffect(() => {
    if (
      isOrderStatusEnabled &&
      orderDetails?.DeliveryType === Api.DeliveryType.Delivery &&
      deliveryRestaurants
    ) {
      const deliveryRestaurantKey = deliveryKey(
        orderDetails.VirtualRestaurantId,
        orderDetails.DeliveryLocationId,
      );
      if (!Object.keys(deliveryRestaurants).includes(deliveryRestaurantKey)) {
        actions.getRestaurantData(
          orderDetails.VirtualRestaurantId,
          'delivery',
          orderDetails.DeliveryLocationId,
        );
      }
    }
  }, [orderDetails, deliveryRestaurants]);

  if (appConfig.status !== 'success' || !orderDetails) {
    return <LoadingSpinner />;
  }

  const isDelivery = orderDetails?.DeliveryType === Api.DeliveryType.Delivery;

  const address = isDelivery
    ? orderDetails.DeliveryLocationAddressString
    : `${orderDetails?.RestaurantName} ${orderDetails?.PickupLocationAddressString}`;

  const pageTitle = isDelivery
    ? translate('OrderHistoryDetail_pageTitle_deliveryOrder')
    : translate('OrderHistoryDetail_pageTitle_collectionOrder');

  const headerProps = !isOrderStatusEnabled
    ? {
        className: classes.header,
        title: pageTitle as string,
        titleTestId: 'history-detail-title',
      }
    : {};

  const trackOrderStatus = (fulfilmentStatus: FulfilmentStatus): void => {
    if (fulfilmentStatus) {
      const eventData = getOrderStatusViewedEventData(
        fulfilmentStatus,
        orderDetails?.OrderId,
      );
      analyticsService.track(eventData);
    }
  };

  const trackBackSelected = (): void => {
    const eventData = {
      amp_label: 'Back Selected',
      amp_view: 'Order History Details',
    };
    analyticsService.track(eventData);
  };

  const renderOrderDetails = (): JSX.Element => (
    <OrderDescriptionList
      address={address}
      deliveryType={orderDetails?.DeliveryType}
      hasOrderTime={!!orderTime}
      isError={orderStatus?.IsError}
      orderId={orderDetails?.OrderId}
      restaurantName={orderDetails?.RestaurantName}
      timeOfOrder={orderTime}
      translate={translate}
    />
  );

  const localizedPaymentMethodLabel =
    orderHistoryUtils.localizePaymentMethodLabel(
      orderDetails?.PaymentAccountDescription,
      translate,
    );

  const renderActionButtons = (): JSX.Element => {
    if (isMobileScreen) {
      return (
        <BottomBar fixedOnly={['xs', 'sm']}>
          <div className={classes.receiptButtonContainer}>
            {hasReceiptCode && (
              <ReceiptButton
                receiptCode={orderDetails?.ReceiptCode}
                translate={translate}
                variant={isReorderEnabled ? 'secondary' : 'primary'}
              />
            )}
            {showReorderButton && (
              <Button
                className={classes.reorderButton}
                onClick={(): void =>
                  reorder(
                    orderDetails,
                    actions.setBasket,
                    actions.setReorderOrderId,
                    'Order history',
                  )
                }
              >
                {translate('ReorderCard_Reorder')}
              </Button>
            )}
          </div>
        </BottomBar>
      );
    }
    return (
      <div className={classes.receiptButtonContainer}>
        {showReorderButton && (
          <Button
            className={classes.reorderButton}
            onClick={(): void =>
              reorder(
                orderDetails,
                actions.setBasket,
                actions.setReorderOrderId,
                'Order history',
              )
            }
          >
            {translate('ReorderCard_Reorder')}
          </Button>
        )}
        {hasReceiptCode && (
          <ReceiptButton
            receiptCode={orderDetails?.ReceiptCode}
            translate={translate}
            variant={isReorderEnabled ? 'secondary' : 'primary'}
          />
        )}
      </div>
    );
  };

  const reorderSnackbarMessage =
    menu &&
    isReorderAvailable &&
    !areAllOrderItemsUnavailable &&
    !isOrderDeliveryAndDeliveryLocationDoesNotExist
      ? translate('Reorder_PricesMightBeDifferent')
      : translate('Reorder_Unavailable');

  return (
    <Page pageName="history-details">
      <PageHeader showProfileMenu {...headerProps}>
        <BackLink
          label={translate('Back') as string}
          linkPath="/history"
          onClick={trackBackSelected}
        />
      </PageHeader>

      <article className="fd-content__main">
        {isOrderStatusEnabled && (
          <OrderStatus orderStatus={orderStatus} translate={translate} />
        )}
        {renderOrderDetails()}

        <ul className={classes.orderDetailList}>
          {orderDetails && (
            <HistoryItemsList
              currency={orderDetails.Currency}
              languageCode={currentLang}
              orderItems={orderDetails?.OrderHistoryItems}
              translate={translate}
              unavailableOrderItemIds={unavailableOrderItemIds.map(
                (x) => x.orderItemId,
              )}
            />
          )}
          {orderDetails?.OnlineOrderingFee > 0 && (
            <li>
              <OrderSummaryLine
                currency={orderDetails.Currency}
                label={translate('Online_ordering_fee') as string}
                languageCode={currentLang}
                translate={translate}
                value={orderDetails?.OnlineOrderingFee}
              />
            </li>
          )}
          {orderDetails?.DeliveryAmount > 0 && (
            <li>
              <OrderSummaryLine
                currency={orderDetails.Currency}
                label={translate('Delivery_fee') as string}
                languageCode={currentLang}
                translate={translate}
                value={orderDetails?.DeliveryAmount}
              />
            </li>
          )}
          {orderDetails?.TotalTax > 0 && (
            <li>
              <OrderSummaryLine
                currency={orderDetails.Currency}
                label={getTaxLabel(appConfig.data, translate)}
                languageCode={currentLang}
                translate={translate}
                value={orderDetails?.TotalTax}
              />
            </li>
          )}
          {orderDetails?.TipAmount > 0 && (
            <li>
              <OrderSummaryLine
                currency={orderDetails.Currency}
                label={translate('Tip') as string}
                languageCode={currentLang}
                translate={translate}
                value={orderDetails?.TipAmount}
              />
            </li>
          )}
          {!!orderDetails?.VoucherAmount && (
            <li>
              <OrderSummaryLine
                currency={orderDetails.Currency}
                label={`${translate('Voucher')} ${orderDetails?.VoucherCode}`}
                languageCode={currentLang}
                translate={translate}
                value={orderDetails?.VoucherAmount}
              />
            </li>
          )}
          <li>
            <OrderSummaryLine
              bigPrice
              currency={orderDetails.Currency}
              label={translate('Total') as string}
              subLabel={localizedPaymentMethodLabel}
              languageCode={currentLang}
              translate={translate}
              value={orderDetails.TotalAmount}
            />
          </li>
        </ul>
        {restaurantPhoneNumber && isOrderStatusEnabled && (
          <ContactRestaurantInfoBox
            restaurantPhoneNumber={restaurantPhoneNumber}
            translate={translate}
          />
        )}
        {isReorderEnabled && (
          <Snackbar
            className={classes.snackbar}
            message={reorderSnackbarMessage as string}
            type="secondary"
            variant="info"
          />
        )}
        {renderActionButtons()}
      </article>
    </Page>
  );
}
